/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  DialogContent,
  DialogTitle,
  Menu,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  InputAdornment,
  Grid,
  Button,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useState } from 'react';
import {
  CustomIconButton,
  CustomTooltip,
  dateUtils,
  FilterChip,
} from '@opengeoweb/shared';
import { ChevronDown, Clock, Close } from '@opengeoweb/theme';
import type { TimeFilter } from './filter-utils';
import { TimeFilterMode } from './filter-utils';
import { useWarningsTranslation } from '../../utils/i18n';

interface TimeFilterChipProps {
  timeFilter: TimeFilter;
  onUpdateTimeFilter: (timeFilter: TimeFilter) => void;
}

const TimeFilterChip: React.FC<TimeFilterChipProps> = ({
  timeFilter,
  onUpdateTimeFilter,
}) => {
  const { t } = useWarningsTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempCustomStart, setTempCustomStart] = useState<string | undefined>();
  const [tempCustomEnd, setTempCustomEnd] = useState<string | undefined>();

  const isSelected = timeFilter.mode !== TimeFilterMode.all;
  const pickerStartValue = tempCustomStart ?? timeFilter.customStart;
  const pickerEndValue = tempCustomEnd ?? timeFilter.customEnd;

  const handleModeChange = (mode: TimeFilterMode): void => {
    if (mode === TimeFilterMode.custom) {
      setTempCustomStart(
        timeFilter.customStart ?? new Date(Date.now()).toISOString(),
      );
      setTempCustomEnd(
        timeFilter.customEnd ?? new Date(Date.now()).toISOString(),
      );
    }
    const newFilter = {
      mode,
      customStart:
        mode === TimeFilterMode.custom ? timeFilter.customStart : undefined,
      customEnd:
        mode === TimeFilterMode.custom ? timeFilter.customEnd : undefined,
    };
    onUpdateTimeFilter(newFilter);
  };

  const handleTempTimeChange = (
    field: 'customStart' | 'customEnd',
    value: Date | null,
  ): void => {
    if (!value) {
      if (field === 'customStart') {
        setTempCustomStart(undefined);
      } else {
        setTempCustomEnd(undefined);
      }
      return;
    }
    const isoString = dateUtils.setUTCDate(value)?.toISOString();
    if (field === 'customStart') {
      setTempCustomStart(isoString);
    } else {
      setTempCustomEnd(isoString);
    }
  };

  const getPickerValue = (isoString: string | undefined): Date | null => {
    if (!isoString) {
      return null;
    }
    const utc = new Date(isoString);
    return new Date(
      utc.getUTCFullYear(),
      utc.getUTCMonth(),
      utc.getUTCDate(),
      utc.getUTCHours(),
      utc.getUTCMinutes(),
    );
  };

  const handleApply = (): void => {
    onUpdateTimeFilter({
      mode: TimeFilterMode.custom,
      customStart: tempCustomStart,
      customEnd: tempCustomEnd,
    });
    setAnchorEl(null);
  };

  const getTimeFilterLabel = (
    mode: TimeFilterMode,
    t: (key: string) => string,
  ): string => {
    const baseLabel = t('filter-time');

    if (mode === TimeFilterMode.all) {
      return baseLabel;
    }

    if (mode === TimeFilterMode.custom) {
      if (!timeFilter.customStart || !timeFilter.customEnd) {
        return `${baseLabel} (${t('filter-time-custom')})`;
      }

      const currentYear = new Date().getUTCFullYear();
      const startYear = dateUtils.getYear(timeFilter.customStart);
      const endYear = dateUtils.getYear(timeFilter.customEnd);

      const includeYear = startYear !== currentYear || endYear !== currentYear;
      const dateFormat = includeYear
        ? dateUtils.DATE_FORMAT_DATEPICKER
        : dateUtils.DATE_FORMAT_DATEPICKER_NO_YEAR;

      const start = dateUtils.dateToString(
        new Date(timeFilter.customStart),
        dateFormat,
      );
      const end = dateUtils.dateToString(
        new Date(timeFilter.customEnd),
        dateFormat,
      );

      return `${baseLabel} (${start} - ${end})`;
    }
    return `${baseLabel} (${t(`filter-time-${mode}`)})`;
  };

  return (
    <>
      <CustomTooltip placement="bottom" title={t('filter-time')}>
        <Box>
          <FilterChip
            actionIcon={
              <Grid
                container
                style={{
                  marginRight: '8px',
                  minWidth: '24px',
                }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <ChevronDown fontSize="small" sx={{ ml: 1 }} />
              </Grid>
            }
            id="time"
            isSelected={isSelected}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock sx={{ mr: 1 }} />
                {getTimeFilterLabel(timeFilter.mode, t)}
              </Box>
            }
            onClick={(ref: HTMLElement | null) => setAnchorEl(ref)}
            onClickIcon={(ref: HTMLElement | null) => setAnchorEl(ref)}
            size="medium"
          />
        </Box>
      </CustomTooltip>
      <Menu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        slotProps={{ backdrop: { style: { opacity: 0, transition: 'none' } } }}
        sx={{ padding: 3 }}
      >
        <DialogTitle
          id="dialog-title"
          sx={{
            zIndex: 1,
            padding: '8px 18px 16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '20px',
              fontWeight: 500,
              paddingRight: 1,
            }}
          >
            {t('filter-time')}
          </Typography>
          <CustomIconButton
            aria-label={t('close-dialog-confirm')}
            onClick={() => setAnchorEl(null)}
            sx={{
              '&.MuiIconButton-root': {
                color: 'geowebColors.greys.accessible',
              },
            }}
          >
            <Close />
          </CustomIconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '0px 24px 16px 24px',
          }}
        >
          <RadioGroup
            onChange={(e) => handleModeChange(e.target.value as TimeFilterMode)}
            value={timeFilter.mode}
          >
            <FormControlLabel
              control={<Radio />}
              label={t('filter-time-no-filter')}
              value={TimeFilterMode.all}
            />
            <FormControlLabel
              control={<Radio />}
              label={t('filter-time-today')}
              value={TimeFilterMode.today}
            />
            <FormControlLabel
              control={<Radio />}
              label={t('filter-time-tomorrow')}
              value={TimeFilterMode.tomorrow}
            />
            <FormControlLabel
              control={<Radio />}
              label={t('filter-time-custom')}
              value={TimeFilterMode.custom}
            />
          </RadioGroup>
          {timeFilter.mode === TimeFilterMode.custom && (
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',

                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ mr: 1 }}>{t('filter-start')}</Typography>
                  <DateTimePicker
                    ampm={false}
                    format={dateUtils.DATE_FORMAT_DATEPICKER}
                    label={t('filter-choose-date-time')}
                    onChange={(value) =>
                      handleTempTimeChange('customStart', value)
                    }
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        slotProps: {
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                UTC
                              </InputAdornment>
                            ),
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        openPickerButtonPosition: 'start',
                      },
                    }}
                    timeSteps={{ minutes: 1 }}
                    value={
                      getPickerValue(pickerStartValue) ?? new Date(Date.now())
                    }
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ mr: 1 }}>{t('filter-end')}</Typography>
                  <DateTimePicker
                    ampm={false}
                    format={dateUtils.DATE_FORMAT_DATEPICKER}
                    label={t('filter-choose-date-time')}
                    onChange={(value) =>
                      handleTempTimeChange('customEnd', value)
                    }
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        slotProps: {
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                UTC
                              </InputAdornment>
                            ),
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        openPickerButtonPosition: 'start',
                      },
                    }}
                    timeSteps={{ minutes: 1 }}
                    value={getPickerValue(pickerEndValue)}
                  />
                </Box>
              </LocalizationProvider>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={handleApply}
                  sx={{
                    textTransform: 'none',
                    px: 6,
                    py: 1,
                    backgroundColor: 'geowebColors.buttons.tool.active.fill',
                    color: 'white',
                  }}
                >
                  {t('filter-time-apply')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Menu>
    </>
  );
};

export default TimeFilterChip;
